import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/brand";
import {
  ShieldStar,
  MagnifyingGlass,
  ArrowClockwise,
  X,
  UserPlus,
  UserSwitch,
  Copy,
  Trash,
  CaretUp,
  CaretDown,
  ClockCounterClockwise,
} from "@phosphor-icons/react";

// ============================================================================
// /admin dashboard (Iter30)
// Gated by user.is_admin. Single source of truth for user management.
//   • Create User (email + first name) — invites via email, bypasses Stripe
//   • Grant VIP (lifetime OR N months) — no Stripe involved
//   • Revoke VIP — drops user back to fresh 10-day trial
//   • Impersonate — SUPER-ADMINs only (admin@ + support@), 2h session
// ============================================================================

// Only these emails see the Impersonate button. Server enforces this too.
const SUPER_ADMINS = new Set(["admin@forecastly.me", "support@forecastly.me"]);

export default function AdminPage() {
  const { user, setAuthFromToken } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [action, setAction] = useState(null); // { user, mode: 'grant' | 'revoke' | 'delete' | 'restore' }
  const [showCreate, setShowCreate] = useState(false);
  const [showTestEmail, setShowTestEmail] = useState(false);
  // Iter31: default sort = Email ascending so admin accounts (which start with
  // 'a') pin to the top of the table regardless of who signed up last.
  const [sort, setSort] = useState({ key: "email", dir: "asc" });
  const [impersonations, setImpersonations] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [usersRes, impRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/impersonations`).catch(() => ({ data: { sessions: [] } })),
      ]);
      setRows(usersRes.data.users || []);
      setImpersonations(impRes.data.sessions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_admin]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const arr = rows.filter((r) => {
      if (planFilter && r.plan !== planFilter) return false;
      if (!q) return true;
      return (
        (r.email || "").toLowerCase().includes(q) ||
        (r.name || "").toLowerCase().includes(q) ||
        (r.business_name || "").toLowerCase().includes(q)
      );
    });
    // Iter31: table sortable by any of these keys.
    const { key, dir } = sort;
    const sign = dir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const av = a[key] || "";
      const bv = b[key] || "";
      if (av < bv) return -1 * sign;
      if (av > bv) return 1 * sign;
      return 0;
    });
    return arr;
  }, [rows, query, planFilter, sort]);

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  };

  const stats = useMemo(() => {
    const s = { total: rows.length, trial: 0, subscription: 0, annual: 0, vip: 0, other: 0 };
    rows.forEach((r) => {
      if (r.vip_lifetime) s.vip += 1;
      else if (r.plan === "trial") s.trial += 1;
      else if (r.plan === "subscription") s.subscription += 1;
      else if (r.plan === "annual") s.annual += 1;
      else s.other += 1;
    });
    return s;
  }, [rows]);

  const isSuperAdmin = SUPER_ADMINS.has((user?.email || "").toLowerCase());

  const impersonate = async (target) => {
    try {
      const res = await axios.post(`${API}/admin/impersonate`, { user_id: target.id });
      // Save the admin's current token so we can hop back later.
      const currentToken = localStorage.getItem("cc_token");
      if (currentToken) localStorage.setItem("cc_admin_token", currentToken);
      // Swap axios/localStorage to the impersonation token and reload as target.
      setAuthFromToken(res.data.token, res.data.target_user);
      toast.success(`You are now impersonating ${target.email}. Session lasts 2h.`);
      nav("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start impersonation.");
    }
  };

  // Hard-block non-admins — server enforces too, but this stops UI flashing.
  if (user && !user.is_admin) return <Navigate to="/dashboard" replace />;

  return (
    <div className="space-y-6" data-testid="admin-page">
      {/* Header row: title on the left, single Refresh utility on the right so
          the eye lands on the H1 first, not a wall of buttons. Primary/nav
          actions live in their own row below. */}
      <div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="section-eyebrow flex items-center gap-2">
              <ShieldStar size={16} weight="bold" /> Admin
            </div>
            <h1 className="font-heading uppercase text-4xl mt-2">User management</h1>
          </div>
          <button
            type="button"
            className="btn-secondary shrink-0 mt-1"
            onClick={load}
            disabled={loading}
            data-testid="admin-reload"
            title="Reload users"
          >
            <ArrowClockwise size={14} weight="bold" /> {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
        <p className="text-black/75 mt-3 leading-relaxed">
          Every forecastly user at a glance. From here you can create a beta tester by hand
          (bypasses Stripe entirely), grant lifetime or time-limited VIP access, or impersonate a
          user's account to reproduce a support ticket exactly as they see it (super-admins only,
          fully audited below). Deletions are permanent and irreversible — always double-check
          the email before you confirm. Soft-deleted users can still be restored within a 60-day
          window using the green "Restore" button. This page is not linked from the public nav —
          it lives under your account menu so it stays quietly out of the way when you're using
          the product as a regular user.
        </p>
      </div>

      {/* Action / nav strip — separated from the header so Refresh isn't buried
          in the same visual pack. */}
      <div className="flex gap-2 flex-wrap" data-testid="admin-actions">
        <button
          type="button"
          className="btn-primary"
          onClick={() => setShowCreate(true)}
          data-testid="admin-create-user-btn"
        >
          <UserPlus size={14} weight="bold" /> Create user
        </button>
        <a
          href="/admin/tickets"
          className="btn-secondary"
          data-testid="admin-tickets-link"
        >
          🎟 Tickets
        </a>
        <a
          href="/admin/roadmap"
          className="btn-secondary"
          data-testid="admin-roadmap-link"
        >
          🚀 Roadmap
        </a>
        <a
          href="/admin/faqs"
          className="btn-secondary"
          data-testid="admin-faqs-link"
        >
          ❓ Edit FAQs
        </a>
        <a
          href="/admin/events"
          className="btn-secondary"
          data-testid="admin-events-link"
        >
          🎥 Events
        </a>
        <a
          href="/admin/ai-cost"
          className="btn-secondary"
          data-testid="admin-ai-cost-link"
        >
          💡 AI cost
        </a>
        <a
          href="/admin/setup-videos"
          className="btn-secondary"
          data-testid="admin-setup-videos-link"
        >
          🎬 Setup videos
        </a>
        <a
          href="/admin/campaigns"
          className="btn-secondary"
          data-testid="admin-campaigns-link"
        >
          🎁 Campaigns
        </a>
        <a
          href="/admin/articles"
          className="btn-secondary"
          data-testid="admin-articles-link"
        >
          📝 Articles
        </a>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setShowTestEmail(true)}
          data-testid="admin-test-email-btn"
        >
          📧 Send test
        </button>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="admin-stats">
        <StatBox label="Total" value={stats.total} />
        <StatBox label="Trial" value={stats.trial} accent />
        <StatBox label="Subscription" value={stats.subscription} />
        <StatBox label="Annual" value={stats.annual} />
        <StatBox label="Lifetime VIP" value={stats.vip} />
      </div>

      {/* Filter bar */}
      <div className="card flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <MagnifyingGlass size={18} weight="bold" />
          <input
            type="text"
            placeholder="Search email, name or business…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="input !w-full"
            data-testid="admin-search"
          />
        </div>
        <select
          className="input !w-auto"
          value={planFilter}
          onChange={(e) => setPlanFilter(e.target.value)}
          data-testid="admin-plan-filter"
        >
          <option value="">All plans</option>
          <option value="trial">Trial</option>
          <option value="subscription">Subscription</option>
          <option value="annual">Annual</option>
          <option value="past_due">Past due</option>
          <option value="cancelled">Cancelled</option>
          <option value="read_only">Read-only</option>
          <option value="soft_deleted">Soft-deleted</option>
        </select>
        <div className="text-xs text-black/55" data-testid="admin-count">
          {filtered.length} of {rows.length}
        </div>
      </div>

      {/* Users table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-table">
            <thead className="bg-black/5">
              <tr className="text-left">
                <SortableTh sortKey="email" label="Email · Name" sort={sort} onClick={toggleSort} />
                <Th>Plan</Th>
                <SortableTh sortKey="trial_ends_at" label="Trial ends" sort={sort} onClick={toggleSort} />
                <Th>VIP</Th>
                <SortableTh sortKey="created_at" label="Signed up" sort={sort} onClick={toggleSort} />
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/10">
              {loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-black/55">
                    Loading users…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-black/55">
                    No users match those filters.
                  </td>
                </tr>
              ) : (
                filtered.map((r) => (
                  <tr key={r.id} data-testid={`admin-row-${r.email}`} className="hover:bg-[#FFF7E5]">
                    <Td>
                      <div className="font-semibold">{r.email}</div>
                      <div className="text-xs text-black/55 flex flex-wrap items-center gap-1">
                        <span>{r.name || "—"}</span>
                        {r.business_name && <span>· {r.business_name}</span>}
                        {r.is_admin && (
                          <span className="ml-1 text-[9px] font-heading uppercase tracking-widest bg-black text-[#FFBD4A] px-1.5 py-0.5 rounded">
                            Admin
                          </span>
                        )}
                        {r.admin_invited && !r.last_login_at && (
                          <span
                            className="ml-1 text-[9px] font-heading uppercase tracking-widest bg-[#FFBD4A] text-black px-1.5 py-0.5 rounded border border-black"
                            title="Manually created by an admin, has not yet signed in"
                          >
                            Invited
                          </span>
                        )}
                        {r.signup_promo_code && (
                          <span
                            className="ml-1 text-[9px] font-heading uppercase tracking-widest bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded border border-emerald-700"
                            title={`Signed up with promo code ${r.signup_promo_code}${r.signup_anchor_shift_days ? ` · +${r.signup_anchor_shift_days} days on first billing cycle` : ""}`}
                            data-testid={`admin-promo-chip-${r.email}`}
                          >
                            {r.signup_promo_code}
                          </span>
                        )}
                      </div>
                    </Td>
                    <Td>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <PlanChip plan={r.plan} />
                        {r.price_label && r.price_label !== "—" && (
                          <span
                            className="text-[9px] uppercase tracking-widest font-heading px-1.5 py-0.5 rounded bg-black/5 text-black/70 border border-black/15 whitespace-nowrap"
                            data-testid={`admin-price-${r.email}`}
                            title="Effective price — spot VIPs, promo codes, grandfathered users"
                          >
                            {r.price_label}
                          </span>
                        )}
                      </div>
                    </Td>
                    <Td className="whitespace-nowrap">
                      {r.trial_ends_at
                        ? new Date(r.trial_ends_at).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : "—"}
                    </Td>
                    <Td>
                      {r.vip_lifetime ? (
                        <span className="text-emerald-700 font-semibold text-xs">Lifetime</span>
                      ) : r.vip_until ? (
                        <span className="text-xs">
                          Until {new Date(r.vip_until).toLocaleDateString("en-GB")}
                        </span>
                      ) : (
                        <span className="text-black/40 text-xs">—</span>
                      )}
                    </Td>
                    <Td className="whitespace-nowrap">
                      {r.created_at
                        ? new Date(r.created_at).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : "—"}
                    </Td>
                    <Td>
                      <div className="flex flex-wrap gap-2">
                        {r.vip_lifetime || r.vip_until ? (
                          <button
                            type="button"
                            className="btn-secondary !px-2 !py-1 !text-xs"
                            onClick={() => setAction({ user: r, mode: "revoke" })}
                            disabled={r.is_admin}
                            title={r.is_admin ? "Cannot revoke admin account" : "Revoke VIP"}
                            data-testid={`admin-revoke-${r.email}`}
                          >
                            Revoke VIP
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn-primary !px-2 !py-1 !text-xs"
                            onClick={() => setAction({ user: r, mode: "grant" })}
                            data-testid={`admin-grant-${r.email}`}
                          >
                            Grant VIP
                          </button>
                        )}
                        {isSuperAdmin && !r.is_admin && (
                          <button
                            type="button"
                            className="btn-secondary !px-2 !py-1 !text-xs"
                            onClick={() => impersonate(r)}
                            title="Log in as this user (2h session, audited)"
                            data-testid={`admin-impersonate-${r.email}`}
                          >
                            <UserSwitch size={12} weight="bold" /> Impersonate
                          </button>
                        )}
                        {!r.is_admin && r.plan === "soft_deleted" && (
                          <button
                            type="button"
                            className="!px-2 !py-1 !text-xs rounded-lg border-[1.5px] border-emerald-700 text-emerald-800 hover:bg-emerald-50 font-semibold inline-flex items-center gap-1"
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/admin/users/${r.id}/restore`);
                                toast.success(`Restored ${r.email} — 30-day grace clock reset`);
                                load();
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || "Restore failed");
                              }
                            }}
                            title="Restore this soft-deleted account (within 60-day window)"
                            data-testid={`admin-restore-${r.email}`}
                          >
                            Restore
                          </button>
                        )}
                        {!r.is_admin && (
                          <button
                            type="button"
                            className="!px-2 !py-1 !text-xs rounded-lg border-[1.5px] border-red-600 text-red-700 hover:bg-red-50 font-semibold inline-flex items-center gap-1"
                            onClick={() => setAction({ user: r, mode: "delete" })}
                            title="Permanently delete this account (data wipe + Stripe cancel)"
                            data-testid={`admin-delete-${r.email}`}
                          >
                            <Trash size={12} weight="bold" /> Delete
                          </button>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent impersonations audit log */}
      {impersonations.length > 0 && (
        <div className="card" data-testid="admin-impersonation-log">
          <h2 className="font-heading uppercase text-2xl flex items-center gap-2">
            <ClockCounterClockwise size={22} weight="duotone" /> Recent impersonations
          </h2>
          <p className="mt-2 text-sm text-black/65 leading-relaxed max-w-2xl">
            Every impersonation session is logged here — showing which admin logged in
            as which user, and when. Last 50 sessions.
          </p>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/5">
                <tr className="text-left">
                  <Th>When</Th>
                  <Th>Admin</Th>
                  <Th>Impersonated</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/10">
                {impersonations.map((s) => (
                  <tr key={s.id}>
                    <Td className="whitespace-nowrap text-xs">
                      {s.started_at
                        ? new Date(s.started_at).toLocaleString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "—"}
                    </Td>
                    <Td className="text-xs">{s.admin_email || "—"}</Td>
                    <Td className="text-xs">{s.target_email || "—"}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {action && (
        <ActionModal
          target={action.user}
          mode={action.mode}
          onClose={() => setAction(null)}
          onDone={() => {
            setAction(null);
            load();
          }}
        />
      )}

      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            load();
          }}
        />
      )}

      {showTestEmail && (
        <SendTestEmailModal onClose={() => setShowTestEmail(false)} />
      )}
    </div>
  );
}

function SendTestEmailModal({ onClose }) {
  const [template, setTemplate] = useState("welcome");
  const [busy, setBusy] = useState(false);
  const templates = [
    { key: "password_reset", label: "1 · Password reset" },
    { key: "actuals_nudge", label: "2 · Actuals nudge (monthly reminder)" },
    { key: "welcome", label: "3 · Welcome / trial started (default)" },
    { key: "welcome_with_offer", label: "3b · Welcome / trial started (URL-campaign variant with offer block)" },
    { key: "welcome_with_referral_offer", label: "3c · Welcome / trial started (referral variant — warm, personal)" },
    { key: "trial_converted", label: "4 · Trial converted → paying (monthly £19)" },
    { key: "trial_converted_annual", label: "4b · Trial converted → paying (annual £190)" },
    { key: "cancellation", label: "5 · Cancellation confirmed" },
    { key: "goodbye", label: "6 · Goodbye (GDPR full data delete)" },
    { key: "forecast_pdf", label: "7 · Forecast PDF export (attachment)" },
    { key: "admin_invite", label: "8 · Admin invite (set password link)" },
    { key: "mid_trial_nudge_incomplete", label: "10a · Mid-trial nudge — onboarding incomplete" },
    { key: "mid_trial_checkin_complete", label: "10b · Mid-trial check-in — onboarding complete" },
    { key: "trial_ending", label: "11 · Trial ending in 3 days (monthly £19)" },
    { key: "trial_ending_annual", label: "11b · Trial ending in 3 days (annual £190)" },
    { key: "payment_failed", label: "13 · Payment failed / card declined" },
    { key: "vip_expiring", label: "14 · VIP expiring in 7 days" },
  ];

  const send = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/emails/send-test`, { template });
      toast.success(`Test email sent — check your inbox`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send test");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell onClose={onClose} title="Send test email">
      <p className="mt-3 text-sm text-black/75 leading-relaxed">
        Fires a template against your inbox using dummy data (name = you, sample dates, sample
        amounts). Great for eyeballing layout in Gmail / Outlook / mobile before shipping.
      </p>
      <label className="block mt-4 text-xs font-semibold">Template</label>
      <select
        className="input mt-1"
        value={template}
        onChange={(e) => setTemplate(e.target.value)}
        data-testid="test-email-template"
      >
        {templates.map((t) => (
          <option key={t.key} value={t.key}>
            {t.label}
          </option>
        ))}
      </select>
      <div className="mt-6 flex gap-3">
        <button
          type="button"
          className="btn-primary"
          onClick={send}
          disabled={busy}
          data-testid="test-email-send"
        >
          {busy ? "Sending…" : "Send to my inbox"}
        </button>
        <button type="button" className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </ModalShell>
  );
}

function StatBox({ label, value, accent }) {
  return (
    <div
      className={`card !p-4 text-center ${accent ? "bg-[#FFF7E5] border-2 border-black" : ""}`}
    >
      <div className="text-xs uppercase tracking-widest text-black/55 font-semibold">{label}</div>
      <div className="font-heading text-3xl mt-1">{value}</div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-xs uppercase tracking-widest font-semibold ${className}`}>{children}</th>;
}

function SortableTh({ sortKey, label, sort, onClick }) {
  const active = sort.key === sortKey;
  return (
    <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">
      <button
        type="button"
        onClick={() => onClick(sortKey)}
        className={`inline-flex items-center gap-1 hover:text-black ${active ? "text-black" : "text-black/70"}`}
        data-testid={`admin-sort-${sortKey}`}
      >
        {label}
        {active && (sort.dir === "asc" ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)}
      </button>
    </th>
  );
}

function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
}

function PlanChip({ plan }) {
  const style = {
    trial: "bg-[#FFBD4A] text-black",
    subscription: "bg-emerald-100 text-emerald-800",
    annual: "bg-emerald-100 text-emerald-800",
    past_due: "bg-red-100 text-red-800",
    cancelled: "bg-black/10 text-black/60",
    read_only: "bg-black/10 text-black/60",
    soft_deleted: "bg-red-100 text-red-800",
  }[plan] || "bg-black/10 text-black/60";
  return (
    <span className={`text-[10px] uppercase tracking-widest font-heading px-2 py-1 rounded ${style}`}>
      {plan || "—"}
    </span>
  );
}

// ---------- Grant / Revoke / Delete ----------
function ActionModal({ target, mode, onClose, onDone }) {
  const [grantMode, setGrantMode] = useState("lifetime"); // 'lifetime' | 'months'
  const [months, setMonths] = useState(3);
  const [busy, setBusy] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const isGrant = mode === "grant";
  const isRevoke = mode === "revoke";
  const isDelete = mode === "delete";

  const submit = async () => {
    setBusy(true);
    try {
      if (isGrant) {
        const payload = {
          email: target.email,
          months: grantMode === "lifetime" ? 0 : Number(months),
        };
        await axios.post(`${API}/billing/admin/grant`, payload);
        // Iter31: the invite email fires from the server ONLY for admin-invited
        // users who haven't logged in yet, so show a hint if it was sent.
        if (target.admin_invited && !target.last_login_at) {
          toast.success(
            `Access granted to ${target.email} — invite email sent with a set-password link.`,
          );
        } else {
          toast.success(
            grantMode === "lifetime"
              ? `Lifetime VIP granted to ${target.email}.`
              : `${months}-month free access granted to ${target.email}.`,
          );
        }
      } else if (isRevoke) {
        await axios.post(`${API}/admin/users/revoke-vip`, { email: target.email });
        toast.success(`VIP revoked from ${target.email}. Placed on a fresh 10-day trial.`);
      } else if (isDelete) {
        await axios.post(`${API}/admin/users/delete`, {
          user_id: target.id,
          confirm_email: confirmEmail,
        });
        toast.success(`${target.email} permanently deleted.`);
      }
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete that action.");
    } finally {
      setBusy(false);
    }
  };

  const title = isGrant ? "Grant VIP access" : isRevoke ? "Revoke VIP" : "Delete user account";
  const confirmDisabled =
    busy ||
    (isGrant && grantMode === "months" && (!months || Number(months) < 1)) ||
    (isDelete && confirmEmail.trim().toLowerCase() !== (target.email || "").toLowerCase());

  return (
    <ModalShell onClose={onClose} title={title}>
      {isGrant && (
        <>
          <p className="mt-3 text-sm text-black/75 leading-relaxed">
            Grant free access to <strong>{target.email}</strong> — no Stripe, no card capture.
            Pick lifetime (true VIP) or a set number of months.
          </p>
          {target.admin_invited && !target.last_login_at && (
            <p className="mt-3 text-sm text-black/75 leading-relaxed bg-[#FFF7E5] border-[1.5px] border-black/25 rounded-xl p-3">
              This user was manually created by an admin and hasn't signed in yet — an
              invite email with a set-password link will be sent as soon as you confirm,
              describing the access you're granting.
            </p>
          )}
          <div className="mt-4 space-y-3">
            <label className="flex items-start gap-3 cursor-pointer p-3 border-[1.5px] border-black/25 rounded-xl hover:bg-[#FFF7E5]">
              <input
                type="radio"
                name="grant-mode"
                value="lifetime"
                checked={grantMode === "lifetime"}
                onChange={() => setGrantMode("lifetime")}
                className="mt-1"
                data-testid="grant-mode-lifetime"
              />
              <div>
                <div className="font-semibold text-sm">Lifetime access (true VIP)</div>
                <div className="text-xs text-black/55 mt-0.5">
                  Free forever. Never touches Stripe. Full app for as long as they want.
                </div>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer p-3 border-[1.5px] border-black/25 rounded-xl hover:bg-[#FFF7E5]">
              <input
                type="radio"
                name="grant-mode"
                value="months"
                checked={grantMode === "months"}
                onChange={() => setGrantMode("months")}
                className="mt-1"
                data-testid="grant-mode-months"
              />
              <div className="flex-1">
                <div className="font-semibold text-sm">Time-limited (N months)</div>
                <div className="text-xs text-black/55 mt-0.5 mb-2">
                  Free for a set window. When it ends, they'll be prompted to add a card
                  via Stripe to keep going.
                </div>
                <input
                  type="number"
                  min="1"
                  max="120"
                  className="input !w-32"
                  value={months}
                  disabled={grantMode !== "months"}
                  onChange={(e) => setMonths(e.target.value)}
                  data-testid="grant-months-input"
                />
                <span className="ml-2 text-xs text-black/55">months</span>
              </div>
            </label>
          </div>
        </>
      )}
      {isRevoke && (
        <p className="mt-3 text-sm text-black/75 leading-relaxed">
          Revoke VIP from <strong>{target.email}</strong> and drop them back to a fresh
          10-day trial. They won't be locked out — the trial gives them time to add a card.
        </p>
      )}
      {isDelete && (
        <>
          <div className="mt-3 p-3 bg-red-50 border-[1.5px] border-red-600 rounded-xl">
            <p className="text-sm text-red-900 leading-relaxed font-semibold">
              This permanently deletes <strong>{target.email}</strong> and every piece of
              their data — forecast, actuals, goals, insights, everything.
            </p>
            <p className="text-xs text-red-800 leading-relaxed mt-2">
              If they have a live Stripe subscription it will be cancelled automatically.
              This cannot be undone.
            </p>
          </div>
          <div className="mt-4">
            <label className="block text-xs font-semibold mb-1">
              Retype their email to confirm
            </label>
            <input
              type="text"
              className="input"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={target.email}
              autoFocus
              data-testid="admin-delete-confirm"
            />
          </div>
        </>
      )}
      <div className="mt-6 flex gap-3">
        <button
          type="button"
          className={isDelete ? "btn-primary bg-red-600 hover:bg-red-700 border-red-800" : "btn-primary"}
          onClick={submit}
          disabled={confirmDisabled}
          data-testid="admin-action-confirm"
        >
          {busy ? "Working…" : isGrant ? "Grant access" : isRevoke ? "Revoke VIP" : "Delete permanently"}
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={onClose}
          data-testid="admin-action-cancel"
        >
          Cancel
        </button>
      </div>
    </ModalShell>
  );
}

// ---------- Create User ----------
function CreateUserModal({ onClose, onDone }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [invited, setInvited] = useState(null); // { email, set_password_url }

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !name) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/users/create`, {
        email: email.trim().toLowerCase(),
        name: name.trim(),
      });
      setInvited({ email: res.data.email, set_password_url: res.data.set_password_url });
      toast.success(`Account created for ${res.data.email}. Grant VIP next to send their invite.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create that user.");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(invited.set_password_url);
    toast.success("Link copied to clipboard.");
  };

  return (
    <ModalShell onClose={onClose} title={invited ? "User created" : "Create new user"}>
      {invited ? (
        <>
          <p className="mt-3 text-sm text-black/75 leading-relaxed">
            <strong>{invited.email}</strong> has been created. <strong>No email has been
            sent yet</strong> — the invite fires when you press <em>Grant VIP</em> on
            their row so the copy can describe their access (lifetime vs N months).
          </p>
          <p className="mt-3 text-sm text-black/75 leading-relaxed">
            If you want to hand them a link before granting access, this one is valid for 7 days:
          </p>
          <div className="mt-3 p-3 bg-[#FFF7E5] border-[1.5px] border-black/25 rounded-xl text-xs break-all font-mono">
            {invited.set_password_url}
          </div>
          <div className="mt-4 flex gap-2 items-center">
            <button
              type="button"
              className="btn-secondary"
              onClick={copyLink}
              data-testid="admin-copy-link"
            >
              <Copy size={14} weight="bold" /> Copy link
            </button>
            <p className="text-xs text-black/55">
              Next step: press "Grant VIP" on their row to send the invite email.
            </p>
          </div>
          <div className="mt-6 flex gap-3">
            <button
              type="button"
              className="btn-primary"
              onClick={onDone}
              data-testid="admin-create-done"
            >
              Done
            </button>
          </div>
        </>
      ) : (
        <form onSubmit={submit} className="mt-4 space-y-3">
          <p className="text-sm text-black/70 leading-relaxed">
            Create a user account manually — no Stripe, no card capture. They'll receive
            an invite email to set their password and log in. VIP or trial extension can
            be assigned separately once they're in the table.
          </p>
          <div>
            <label className="block text-xs font-semibold mb-1">First name</label>
            <input
              type="text"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Alex"
              required
              autoFocus
              data-testid="admin-create-name"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">Email address</label>
            <input
              type="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="beta@example.co.uk"
              required
              data-testid="admin-create-email"
            />
          </div>
          <div className="mt-6 flex gap-3">
            <button
              type="submit"
              className="btn-primary"
              disabled={busy}
              data-testid="admin-create-submit"
            >
              {busy ? "Creating…" : "Create + send invite"}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              data-testid="admin-create-cancel"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </ModalShell>
  );
}

function ModalShell({ children, onClose, title }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="admin-modal"
    >
      <div
        className="bg-white rounded-2xl border-2 border-black max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-heading uppercase text-xl">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-black/60 hover:text-black"
            data-testid="admin-modal-close"
          >
            <X size={20} weight="bold" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
